import { InboxNotificationItem, NotificationType } from './inboxTypes';
import { listDiscrepancies } from '../../graphql/queries';
import { buildIssuePageTitle, buildIssueDescription, issueNoticeOriginFromRecord } from '../../utils/issueUtils';
import type { IssueNoticeOrigin } from '../../utils/issueUtils';
import { resolveReportSource } from '../../utils/resolveReportSource';
import type { ReportSource } from '../../utils/reportUrlUtils';

/**
 * Builds inbox notification items by aggregating data from multiple sources:
 * - Discrepancies (critical alerts, failed inspections)
 * - PM schedules (upcoming service due)
 * - Training (expiring certifications)
 */
type NamedRecord = { id?: string; name?: string | null };

/** Company unit fields read while building inbox notices. */
type UnitRecord = {
    id?: string | null;
    name?: string | null;
    locationId?: string | null;
    equipmentId?: string | null;
    equipmentName?: string | null;
    nextServiceDate?: string | null;
    nextServiceLevel?: string | null;
};

/** Discrepancy/issue fields read while building inbox notices. */
type IssueRecord = {
    id?: string | null;
    status?: string | null;
    createdAt?: string | null;
    unitId?: string | null;
    equipmentId?: string | null;
    equipmentName?: string | null;
    locationName?: string | null;
    ticketNumber?: string | null;
    reportedBy?: string | null;
    reportedByContact?: string | null;
    reportId?: string | null;
    promptLabel?: string | null;
    promptTitle?: string | null;
    log?: string | null;
};

/**
 * Minimal shape of the Amplify client used here: just the `graphql` call that
 * returns the discrepancy list. Avoids leaking `any` through this module.
 */
export type InboxGraphqlClient = {
    graphql: (operation: {
        query: typeof listDiscrepancies;
        variables: { filter: { companyId: { eq: string } } };
    }) => Promise<{ data: { listDiscrepancies: { items: Array<IssueRecord | null> } } }>;
};

function unitFacts(
    issue: Pick<IssueRecord, 'unitId' | 'equipmentName' | 'locationName'>,
    companyUnits: UnitRecord[],
    locations: NamedRecord[],
) {
    const unit = (companyUnits || []).find((item) => item?.id === issue.unitId);
    const unitName = inboxText(unit?.name);
    const location = locations.find((item) => item.id === unit?.locationId);
    return {
        unitName,
        equipmentLabel: unitName || inboxText(issue.equipmentName) || 'Unknown Equipment',
        locationName: inboxText(location?.name) || inboxText(issue.locationName),
    };
}

function issueNotification(
    issue: IssueRecord,
    type: NotificationType,
    equipmentStatus: string,
    companyUnits: UnitRecord[],
    locations: NamedRecord[],
): InboxNotificationItem {
    const facts = unitFacts(issue, companyUnits, locations);
    const status = inboxText(issue.status) || (type === 'critical-alert' ? 'inop' : 'warn');
    return {
        id: `${type === 'critical-alert' ? 'disc' : 'warn'}-${issue.id}`,
        type,
        title: buildIssuePageTitle(status === 'Open' ? 'warn' : status, facts.equipmentLabel),
        description: buildIssueDescription(status),
        timestamp: inboxText(issue.createdAt),
        relativeTime: getRelativeTime(issue.createdAt),
        equipmentId: inboxText(issue.equipmentId),
        equipmentName: facts.equipmentLabel,
        unitId: inboxText(issue.unitId),
        unitName: facts.unitName,
        locationName: facts.locationName,
        noticeOrigin: issueNoticeOriginFromRecord(issue) || undefined,
        issueId: inboxText(issue.id),
        issueTicket: inboxText(issue.ticketNumber),
        equipmentStatus,
        reportedBy: inboxText(issue.reportedBy),
        reportedByContact: inboxText(issue.reportedByContact),
        reportId: inboxText(issue.reportId),
    };
}

async function fillNoticeOrigins(notifications: InboxNotificationItem[]) {
    // Notices lacking a resolved origin but carrying a reportId (not PM-due).
    const pending = notifications.filter(
        (n) => !n.noticeOrigin && n.reportId && n.type !== 'pm-due',
    );
    if (pending.length === 0) return;

    // Multiple notices can share one report (e.g. several discrepancies from the
    // same inspection). Resolve each distinct reportId ONCE instead of per-notice.
    const sourceByReportId = new Map<string, Promise<ReportSource>>();
    const originFor = async (reportId: string): Promise<IssueNoticeOrigin | undefined> => {
        let lookup = sourceByReportId.get(reportId);
        if (!lookup) {
            lookup = resolveReportSource(reportId);
            sourceByReportId.set(reportId, lookup);
        }
        const source = await lookup;
        if (source === 'InspectionReport') return 'Inspection';
        if (source === 'DailyServiceReport') return 'Daily Service';
        return undefined;
    };

    await Promise.all(
        pending.map(async (notification) => {
            const origin = await originFor(notification.reportId!);
            if (origin) notification.noticeOrigin = origin;
        }),
    );
}

export const buildInboxNotifications = async (
    client: InboxGraphqlClient,
    companyId: string,
    companyUnits: UnitRecord[],
    locations: NamedRecord[] = [],
): Promise<InboxNotificationItem[]> => {
    const notifications: InboxNotificationItem[] = [];

    try {
        // Fetch discrepancies for critical alerts and failed inspections
        const discrepancyResponse = await client.graphql({
            query: listDiscrepancies,
            variables: { filter: { companyId: { eq: companyId } } },
        });

        const discrepancies = (discrepancyResponse.data.listDiscrepancies.items || []).filter(
            (item): item is IssueRecord => Boolean(item),
        );

        // Convert critical discrepancies (inop status) to notifications
        const criticalIssues = discrepancies
            .filter((d) => d?.status === 'inop')
            .sort((a, b) => inboxTime(b.createdAt) - inboxTime(a.createdAt))
            .slice(0, 10);

        criticalIssues.forEach((issue) => {
            notifications.push(issueNotification(issue, 'critical-alert', 'Out of Service', companyUnits, locations));
        });

        const warnings = discrepancies
            .filter((d) => d?.status === 'warn' || d?.status === 'Open')
            .sort((a, b) => inboxTime(b.createdAt) - inboxTime(a.createdAt))
            .slice(0, 5);

        warnings.forEach((issue) => {
            notifications.push(issueNotification(issue, 'failed-inspection', 'Warning', companyUnits, locations));
        });

        // Check for PM overdue units
        const now = new Date();
        (Array.isArray(companyUnits) ? companyUnits : []).forEach((unit) => {
            if (!unit?.nextServiceDate) return;
            const serviceDate = new Date(unit.nextServiceDate);
            const daysUntilDue = Math.ceil((serviceDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

            if (daysUntilDue <= 7 && daysUntilDue >= -30) {
                    notifications.push({
                        id: `pm-${unit.id}`,
                        type: 'pm-due',
                        title: `PM ${daysUntilDue <= 0 ? 'overdue' : `due in ${daysUntilDue} days`} — ${inboxText(unit.name) || 'Unknown Unit'}`,
                        description: `Scheduled preventative maintenance ${daysUntilDue <= 0 ? 'is overdue' : 'is coming up'}. Review service requirements.`,
                        timestamp: inboxText(unit.nextServiceDate),
                        relativeTime:
                            daysUntilDue <= 0
                                ? `${Math.abs(daysUntilDue)} days overdue`
                                : `Due in ${daysUntilDue} days`,
                        equipmentId: inboxText(unit.equipmentId),
                        equipmentName: inboxText(unit.name) || inboxText(unit.equipmentName),
                        unitName: inboxText(unit.name),
                        unitId: inboxText(unit.id),
                        locationName: inboxText(locations.find((item) => item.id === unit.locationId)?.name),
                        noticeOrigin: 'PM',
                        pmDaysUntilDue: daysUntilDue,
                        pmServiceLevel: inboxText(unit.nextServiceLevel),
                    });
            }
        });
        await fillNoticeOrigins(notifications);
    } catch (error) {
        console.error('Error building inbox notifications:', error);
    }

    // Sort all notifications by timestamp (newest first)
    notifications.sort((a, b) => inboxTime(b?.timestamp) - inboxTime(a?.timestamp));

    return notifications;
};

/** Render-safe text. Non-strings become empty so a bad record cannot crash the page. */
export const inboxText = (value: unknown): string => {
    if (typeof value === 'string') return value;
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    return '';
};

const inboxTime = (value: unknown): number => {
    const time = new Date(inboxText(value)).getTime();
    return Number.isNaN(time) ? 0 : time;
};

export const formatInboxTimestamp = (value: unknown): string => {
    const date = new Date(inboxText(value));
    if (Number.isNaN(date.getTime())) return '';
    try {
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        });
    } catch {
        return '';
    }
};

/**
 * Returns a human-readable relative time string (e.g., "3 hours ago", "Yesterday")
 */
export const getRelativeTime = (isoString: string | null | undefined): string => {
    const now = new Date();
    const date = new Date(inboxText(isoString));
    if (Number.isNaN(date.getTime())) return '';
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;
    try {
        return date.toLocaleDateString();
    } catch {
        return '';
    }
};

/**
 * Returns the appropriate badge/label for a notification type
 */
export const getNotificationTypeLabel = (type: NotificationType): string => {
    switch (type) {
        case 'critical-alert':
            return 'CRITICAL ALERT';
        case 'pm-due':
            return 'PM DUE';
        case 'inspection-assigned':
            return 'ASSIGNED';
        case 'tire-replacement':
            return 'COMPLETED';
        case 'module-activated':
            return 'INFO';
        case 'training-expiring':
            return 'TRAINING';
        case 'failed-inspection':
            return 'WARNING';
        default:
            return '';
    }
};

/**
 * Returns the CSS class for a notification type badge
 */
export const getNotificationTypeClass = (type: NotificationType): string => {
    switch (type) {
        case 'critical-alert':
            return 'inbox-badge-critical';
        case 'pm-due':
            return 'inbox-badge-pm';
        case 'inspection-assigned':
            return 'inbox-badge-assigned';
        case 'tire-replacement':
            return 'inbox-badge-success';
        case 'module-activated':
            return 'inbox-badge-info';
        case 'training-expiring':
            return 'inbox-badge-training';
        case 'failed-inspection':
            return 'inbox-badge-warning';
        default:
            return '';
    }
};
